import { createGateway } from '@neureus/ai-gateway';
import type { 
  AIGateway, 
  ChatCompletionRequest, 
  ChatCompletionResponse,
  ChatMessage,
  AIProvider 
} from '@neureus/ai-gateway';
import type {
  ContextResult,
  QueryRequest,
  QueryResponse,
  QueryStreamChunk,
  RAGConfig,
  RAGEnv,
} from '../types';
import { GenerationError } from '../types';
import { generateId, truncateText, formatDuration } from '../core/utils';

/**
 * Answer generation service with context injection and streaming support
 */
export class AnswerGenerator {
  private gateway: AIGateway;
  private env: RAGEnv;
  private config: RAGConfig;

  constructor(env: RAGEnv, config: RAGConfig) {
    this.env = env;
    this.config = config;
    
    // Initialize AI Gateway with generation model configuration
    this.gateway = createGateway(env, {
      routing: {
        primary: config.generation.provider,
        fallbacks: this.getFallbackProviders(config.generation.provider),
        loadBalancing: 'least_latency',
        failoverThreshold: 2,
      },
      cache: {
        enabled: true,
        ttl: 3600, // Cache responses for 1 hour
        strategy: 'exact',
        keyPrefix: 'gen:',
      },
      rateLimit: {
        enabled: true,
        requests: 1000,
        window: 3600,
      },
    });
  }

  /**
   * Generate answer from query and context
   */
  async generateAnswer(
    query: string,
    context: ContextResult,
    options: {
      conversationHistory?: ChatMessage[];
      customPrompt?: string;
      includeSource?: boolean;
      maxTokens?: number;
      temperature?: number;
    } = {}
  ): Promise<QueryResponse> {
    const startTime = Date.now();
    const requestId = generateId('answer');

    try {
      const {
        conversationHistory = [],
        customPrompt,
        includeSource = this.config.generation.includeSource,
        maxTokens = this.config.generation.maxTokens,
        temperature = this.config.generation.temperature,
      } = options;

      // Build the prompt with context
      const prompt = this.buildPrompt(query, context, customPrompt, includeSource);
      
      // Prepare messages for the AI model
      const messages = this.buildMessages(prompt, conversationHistory);
      
      // Create completion request
      const completionRequest: ChatCompletionRequest = {
        model: this.config.generation.model,
        messages,
        temperature,
        maxTokens,
        stream: false,
        cache: false,
      };

      // Generate answer
      const response = await this.gateway.chatCompletion(completionRequest);
      const generationTime = Date.now() - startTime;

      // Extract answer
      const answer = response.choices[0]?.message?.content || 'No answer generated';

      // Build response
      const queryResponse: QueryResponse = {
        answer,
        sources: this.buildSources(context, includeSource),
        context,
        usage: response.usage,
        performance: {
          retrievalTime: context.retrievalTime,
          generationTime,
          totalTime: context.retrievalTime + generationTime,
        },
        requestId,
      };

      return queryResponse;
    } catch (error) {
      throw new GenerationError(
        `Answer generation failed: ${error}`,
        error as Error
      );
    }
  }

  /**
   * Generate streaming answer
   */
  async generateStreamingAnswer(
    query: string,
    context: ContextResult,
    options: {
      conversationHistory?: ChatMessage[];
      customPrompt?: string;
      includeSource?: boolean;
      maxTokens?: number;
      temperature?: number;
      onChunk?: (chunk: QueryStreamChunk) => void;
    } = {}
  ): Promise<ReadableStream<QueryStreamChunk>> {
    const startTime = Date.now();
    const requestId = generateId('stream');

    try {
      const {
        conversationHistory = [],
        customPrompt,
        includeSource = this.config.generation.includeSource,
        maxTokens = this.config.generation.maxTokens,
        temperature = this.config.generation.temperature,
        onChunk,
      } = options;

      // Build the prompt with context
      const prompt = this.buildPrompt(query, context, customPrompt, includeSource);
      
      // Prepare messages for the AI model
      const messages = this.buildMessages(prompt, conversationHistory);
      
      // Create streaming completion request
      const completionRequest: ChatCompletionRequest = {
        model: this.config.generation.model,
        messages,
        temperature,
        maxTokens,
        stream: true,
        cache: false,
      };

      // Get streaming response
      const streamResponse = await this.gateway.chatCompletionStream(completionRequest);
      
      return this.createRAGStream(
        streamResponse,
        context,
        includeSource,
        requestId,
        startTime,
        onChunk
      );
    } catch (error) {
      throw new GenerationError(
        `Streaming answer generation failed: ${error}`,
        error as Error
      );
    }
  }

  /**
   * Generate answer with multi-step reasoning
   */
  async generateReasonedAnswer(
    query: string,
    context: ContextResult,
    options: {
      reasoningSteps?: number;
      includeSource?: boolean;
    } = {}
  ): Promise<QueryResponse & { reasoningSteps: string[] }> {
    const { reasoningSteps = 2, includeSource = true } = options;
    const startTime = Date.now();
    const requestId = generateId('reason');

    try {
      const steps: string[] = [];
      
      // Step 1: Analyze the context
      const analysisPrompt = this.buildAnalysisPrompt(query, context);
      const analysisRequest: ChatCompletionRequest = {
        model: this.config.generation.model,
        messages: [{ role: 'user', content: analysisPrompt }],
        temperature: 0.3,
        maxTokens: 500,
        stream: false,
        cache: false,
      };
      
      const analysisResponse = await this.gateway.chatCompletion(analysisRequest);
      const analysis = analysisResponse.choices[0]?.message?.content || '';
      steps.push(`Analysis: ${analysis}`);

      // Step 2: Generate initial answer
      const answerPrompt = this.buildPrompt(query, context, undefined, includeSource);
      const answerRequest: ChatCompletionRequest = {
        model: this.config.generation.model,
        messages: [
          { role: 'system', content: 'You are a helpful assistant that provides accurate answers based on the given context.' },
          { role: 'user', content: answerPrompt },
          { role: 'assistant', content: `Based on my analysis: ${analysis}\n\nHere's my answer:` },
        ],
        temperature: this.config.generation.temperature,
        maxTokens: this.config.generation.maxTokens,
        stream: false,
        cache: false,
      };

      const answerResponse = await this.gateway.chatCompletion(answerRequest);
      const answer = answerResponse.choices[0]?.message?.content || 'No answer generated';
      steps.push(`Initial Answer: ${answer}`);

      // Step 3: Refine if needed (for more complex reasoning)
      if (reasoningSteps > 2) {
        const refinementPrompt = `
Given the question: "${query}"
And the initial answer: "${answer}"
And the context analysis: "${analysis}"

Please provide a refined, more comprehensive answer that addresses any gaps or improves clarity.
        `.trim();

        const refinementRequest: ChatCompletionRequest = {
          model: this.config.generation.model,
          messages: [{ role: 'user', content: refinementPrompt }],
          temperature: 0.2,
          maxTokens: this.config.generation.maxTokens,
          stream: false,
          cache: false,
        };

        const refinementResponse = await this.gateway.chatCompletion(refinementRequest);
        const refinedAnswer = refinementResponse.choices[0]?.message?.content || answer;
        steps.push(`Refined Answer: ${refinedAnswer}`);
      }

      const generationTime = Date.now() - startTime;

      return {
        answer: steps[steps.length - 1].replace('Refined Answer: ', '').replace('Initial Answer: ', ''),
        sources: this.buildSources(context, includeSource),
        context,
        usage: {
          promptTokens: analysisResponse.usage.promptTokens + answerResponse.usage.promptTokens,
          completionTokens: analysisResponse.usage.completionTokens + answerResponse.usage.completionTokens,
          totalTokens: analysisResponse.usage.totalTokens + answerResponse.usage.totalTokens,
        },
        performance: {
          retrievalTime: context.retrievalTime,
          generationTime,
          totalTime: context.retrievalTime + generationTime,
        },
        requestId,
        reasoningSteps: steps,
      };
    } catch (error) {
      throw new GenerationError(
        `Reasoned answer generation failed: ${error}`,
        error as Error
      );
    }
  }

  /**
   * Validate answer quality and detect hallucinations
   */
  async validateAnswer(
    answer: string,
    query: string,
    context: ContextResult
  ): Promise<{
    isValid: boolean;
    confidence: number;
    issues: string[];
    suggestions: string[];
  }> {
    try {
      const validationPrompt = `
You are an AI answer validator. Your job is to assess whether an AI-generated answer is accurate and grounded in the provided context.

Question: "${query}"

Answer to validate: "${answer}"

Context sources:
${context.chunks.map((chunk, i) => `Source ${i + 1}: ${chunk.content.substring(0, 200)}...`).join('\n\n')}

Please evaluate:
1. Is the answer factually supported by the context? (Yes/No)
2. Are there any potential hallucinations or unsupported claims?
3. Confidence score (0-100) in the answer's accuracy
4. Any specific issues or improvements needed

Respond in this format:
SUPPORTED: [Yes/No]
CONFIDENCE: [0-100]
ISSUES: [List any issues]
SUGGESTIONS: [List improvements]
      `.trim();

      const validationRequest: ChatCompletionRequest = {
        model: this.config.generation.model,
        messages: [{ role: 'user', content: validationPrompt }],
        temperature: 0.1,
        maxTokens: 500,
        stream: false,
        cache: false,
      };

      const response = await this.gateway.chatCompletion(validationRequest);
      const validation = response.choices[0]?.message?.content || '';

      // Parse validation response
      const supportedMatch = validation.match(/SUPPORTED:\s*(Yes|No)/i);
      const confidenceMatch = validation.match(/CONFIDENCE:\s*(\d+)/);
      const issuesMatch = validation.match(/ISSUES:\s*(.+?)(?=SUGGESTIONS:|$)/s);
      const suggestionsMatch = validation.match(/SUGGESTIONS:\s*(.+)$/s);

      const isSupported = supportedMatch?.[1]?.toLowerCase() === 'yes';
      const confidence = confidenceMatch ? parseInt(confidenceMatch[1]) : 50;
      const issues = issuesMatch?.[1]?.trim().split('\n').filter(i => i.trim()) || [];
      const suggestions = suggestionsMatch?.[1]?.trim().split('\n').filter(s => s.trim()) || [];

      return {
        isValid: isSupported && confidence >= 70,
        confidence: confidence / 100,
        issues,
        suggestions,
      };
    } catch (error) {
      console.warn('Answer validation failed:', error);
      return {
        isValid: true, // Default to valid if validation fails
        confidence: 0.5,
        issues: ['Validation failed'],
        suggestions: [],
      };
    }
  }

  /**
   * Build the main prompt with context injection
   */
  private buildPrompt(
    query: string,
    context: ContextResult,
    customPrompt?: string,
    includeSource: boolean = true
  ): string {
    if (customPrompt) {
      return customPrompt.replace('{query}', query).replace('{context}', this.formatContext(context));
    }

    const systemPrompt = this.config.generation.systemPrompt || `
You are a helpful AI assistant that provides accurate, comprehensive answers based on the given context.

Guidelines:
- Answer the question using ONLY the information provided in the context
- Be specific and cite relevant details from the context
- If the context doesn't contain enough information to answer the question, say so clearly
- Do not make up information or speculate beyond what's provided
- Organize your response clearly and logically
${includeSource ? '- Include source references when citing specific information' : ''}
    `.trim();

    const contextText = this.formatContext(context);
    
    const prompt = `
${systemPrompt}

Context:
${contextText}

Question: ${query}

Answer:
    `.trim();

    return prompt;
  }

  /**
   * Build analysis prompt for multi-step reasoning
   */
  private buildAnalysisPrompt(query: string, context: ContextResult): string {
    const contextText = this.formatContext(context);
    
    return `
Analyze the following context to understand how it relates to the question:

Question: "${query}"

Context:
${contextText}

Please provide a brief analysis of:
1. What information in the context is most relevant to the question
2. What key points should be emphasized in the answer
3. Are there any gaps in the context that should be acknowledged

Analysis:
    `.trim();
  }

  /**
   * Format context for inclusion in prompts
   */
  private formatContext(context: ContextResult): string {
    return context.chunks
      .map((chunk, index) => {
        const sourceInfo = chunk.metadata.title || chunk.metadata.documentId || 'Unknown Source';
        const content = truncateText(chunk.content, 500);
        
        return `[Source ${index + 1}: ${sourceInfo}]\n${content}`;
      })
      .join('\n\n');
  }

  /**
   * Build messages array for chat completion
   */
  private buildMessages(prompt: string, conversationHistory: ChatMessage[]): ChatMessage[] {
    const messages: ChatMessage[] = [];
    
    // Add conversation history
    if (conversationHistory.length > 0) {
      messages.push(...conversationHistory);
    }
    
    // Add current prompt
    messages.push({
      role: 'user',
      content: prompt,
    });
    
    return messages;
  }

  /**
   * Build sources array for response
   */
  private buildSources(context: ContextResult, includeSource: boolean) {
    if (!includeSource) {
      return [];
    }

    return context.chunks.map((chunk, index) => ({
      documentId: chunk.metadata.documentId,
      chunkId: chunk.id,
      title: chunk.metadata.title || context.sources[0]?.title,
      url: context.sources.find(s => s.id === chunk.metadata.documentId)?.url,
      relevanceScore: context.relevanceScores[index] || 0,
      content: truncateText(chunk.content, 200),
    }));
  }

  /**
   * Create RAG-specific streaming response
   */
  private createRAGStream(
    aiStream: ReadableStream<Uint8Array>,
    context: ContextResult,
    includeSource: boolean,
    requestId: string,
    startTime: number,
    onChunk?: (chunk: QueryStreamChunk) => void
  ): ReadableStream<QueryStreamChunk> {
    const encoder = new TextEncoder();
    const decoder = new TextDecoder();
    let sentContext = false;
    let sentSources = false;

    return new ReadableStream<QueryStreamChunk>({
      async start(controller) {
        // Send context first
        const contextChunk: QueryStreamChunk = {
          type: 'context',
          data: context,
          requestId,
        };
        controller.enqueue(contextChunk);
        onChunk?.(contextChunk);
        sentContext = true;

        const reader = aiStream.getReader();

        try {
          while (true) {
            const { done, value } = await reader.read();
            
            if (done) {
              // Send sources and completion
              if (!sentSources && includeSource) {
                const sourcesChunk: QueryStreamChunk = {
                  type: 'source',
                  data: this.buildSources(context, includeSource),
                  requestId,
                };
                controller.enqueue(sourcesChunk);
                onChunk?.(sourcesChunk);
              }

              const completeChunk: QueryStreamChunk = {
                type: 'complete',
                data: {
                  performance: {
                    retrievalTime: context.retrievalTime,
                    generationTime: Date.now() - startTime,
                    totalTime: Date.now() - (startTime - context.retrievalTime),
                  },
                },
                requestId,
              };
              controller.enqueue(completeChunk);
              onChunk?.(completeChunk);
              break;
            }

            // Parse SSE data
            const text = decoder.decode(value);
            const lines = text.split('\n');
            
            for (const line of lines) {
              if (line.startsWith('data: ')) {
                const data = line.slice(6);
                if (data === '[DONE]') continue;
                
                try {
                  const parsed = JSON.parse(data);
                  const content = parsed.choices?.[0]?.delta?.content;
                  
                  if (content) {
                    const answerChunk: QueryStreamChunk = {
                      type: 'answer',
                      data: { content },
                      requestId,
                    };
                    controller.enqueue(answerChunk);
                    onChunk?.(answerChunk);
                  }
                } catch (error) {
                  console.warn('Failed to parse streaming chunk:', error);
                }
              }
            }
          }
        } catch (error) {
          const errorChunk: QueryStreamChunk = {
            type: 'error',
            data: { error: error instanceof Error ? error.message : 'Unknown error' },
            requestId,
          };
          controller.enqueue(errorChunk);
          onChunk?.(errorChunk);
          controller.error(error);
        } finally {
          reader.releaseLock();
          controller.close();
        }
      },
    });
  }

  /**
   * Get fallback providers for answer generation
   */
  private getFallbackProviders(primaryProvider: AIProvider): AIProvider[] {
    const allProviders: AIProvider[] = ['openai', 'anthropic', 'google', 'cloudflare'];
    return allProviders.filter(provider => provider !== primaryProvider);
  }

  /**
   * Update generation configuration
   */
  updateConfig(newConfig: Partial<RAGConfig['generation']>): void {
    this.config.generation = { ...this.config.generation, ...newConfig };
    
    // Recreate gateway if provider changed
    if (newConfig.provider && newConfig.provider !== this.config.generation.provider) {
      this.gateway = createGateway(this.env, {
        routing: {
          primary: this.config.generation.provider,
          fallbacks: this.getFallbackProviders(this.config.generation.provider),
          loadBalancing: 'least_latency',
          failoverThreshold: 2,
        },
        cache: {
          enabled: true,
          ttl: 3600,
          strategy: 'exact',
          keyPrefix: 'gen:',
        },
      });
    }
  }

  /**
   * Get generation statistics
   */
  getStats(): {
    provider: AIProvider;
    model: string;
    temperature: number;
    maxTokens: number;
  } {
    return {
      provider: this.config.generation.provider,
      model: this.config.generation.model,
      temperature: this.config.generation.temperature,
      maxTokens: this.config.generation.maxTokens,
    };
  }
}